import * as THREE from 'three';
import { mat } from './common.js';

/**
 * UAV — persistent reconnaissance.
 *
 * Thirty seconds of continuous reveal on every hostile on the map. Unlike the
 * Scout Pulse it has no radius and no decay, so it changes how the whole match is
 * played rather than answering one question.
 *
 * The aircraft itself is real geometry orbiting overhead — it is the only tell an
 * enemy would have, and more practically it is the thing that makes the streak
 * feel called-in rather than toggled on. It is a single small mesh with no
 * shadow-casting, so it costs one draw call.
 */

export const TUNING = {
  duration: 30,
  altitude: 46,
  orbitRadius: 40,
  orbitSpeed: 0.34, // rad/s
  scale: 1.9,
  spawnRise: 3.0, // s of fly-in before the reveal starts
};

export class UAV {
  static id = 'uav';

  constructor(ctx, sys) {
    this.ctx = ctx;
    this.sys = sys;
    this.mesh = null;
    this.t = 0;
    this.phase = 0;
    this.centre = new THREE.Vector3();
  }

  _build() {
    if (this.mesh) return;
    const g = new THREE.Group();
    const hull = mat('hullDark');

    const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.28, 1.5, 4, 10), hull);
    body.rotation.z = Math.PI / 2;
    g.add(body);

    const nose = new THREE.Mesh(new THREE.SphereGeometry(0.3, 10, 8), hull);
    nose.position.x = 0.95;
    nose.scale.set(1.5, 0.85, 0.85);
    g.add(nose);

    const wing = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.07, 5.4), hull);
    wing.position.set(-0.1, 0.06, 0);
    g.add(wing);

    const tail = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.06, 1.5), hull);
    tail.position.set(-1.15, 0.02, 0);
    g.add(tail);

    const fin = new THREE.Mesh(new THREE.BoxGeometry(0.34, 0.7, 0.06), hull);
    fin.position.set(-1.15, 0.34, 0);
    g.add(fin);

    // Two beacons, because at 46 m up the silhouette alone is unreadable.
    for (const s of [-1, 1]) {
      const b = new THREE.Mesh(new THREE.SphereGeometry(0.11, 6, 5), mat(s > 0 ? 'accent' : 'hostile'));
      b.position.set(-0.1, 0.05, s * 2.65);
      g.add(b);
    }

    g.scale.setScalar(TUNING.scale);
    g.name = 'streak-uav';
    g.visible = false;
    this.ctx.scene.add(g);
    this.mesh = g;
  }

  deploy() {
    this._build();
    const p = this.ctx.player;
    this.centre.set(p?.position?.x ?? 0, 0, p?.position?.z ?? 0);
    this.t = 0;
    this.phase = Math.random() * Math.PI * 2;
    this.mesh.visible = true;
    this._revealed = false;

    this.ctx.events.emit('audio:play', { id: 'ui_select', volume: 0.8, pitch: 0.62 });
    return true;
  }

  update(dt) {
    this.t += dt;
    if (this.t >= TUNING.duration) {
      this.end();
      return false;
    }

    this.phase += dt * TUNING.orbitSpeed;
    const r = TUNING.orbitRadius;
    const x = this.centre.x + Math.cos(this.phase) * r;
    const z = this.centre.z + Math.sin(this.phase) * r;
    // Fly in from higher up so the arrival reads as an arrival.
    const rise = Math.min(1, this.t / TUNING.spawnRise);
    const y = TUNING.altitude + (1 - rise) * 26;
    this.mesh.position.set(x, y, z);
    // The airframe is modelled nose-along-+X. On a circle parameterised by
    // (cos φ, sin φ) the velocity is (-sin φ, cos φ), and the yaw that maps local
    // +X onto that is -φ - π/2. The intuitive `-φ + π/2` is the same axis pointed
    // the other way, and flies the drone backwards.
    this.mesh.rotation.set(0, -this.phase - Math.PI / 2, 0);
    this.mesh.rotateX(0.22); // bank into the turn — roll axis is +X on this model

    // Keep the reveal topped up; the system decays it, so a single call per frame
    // is all the coupling required and the streak ending stops it dead.
    this.sys.revealAll(0.4, 'uav', Infinity);
    return true;
  }

  end() {
    if (this.mesh) this.mesh.visible = false;
  }

  dispose() {
    if (!this.mesh) return;
    this.mesh.parent?.remove(this.mesh);
    this.mesh.traverse((o) => o.geometry?.dispose?.());
    this.mesh = null;
  }
}
